// frontend/src/components/ETADisplay.js
// Main ETA Display component - Simplified and refactored version

import { useState, useMemo, useEffect, useRef } from 'react';

// Sub-components
import CompletedTripView from './ETADisplay/CompletedTripView';
import LiveTripView from './ETADisplay/LiveTripView';
import { LoadingView, ErrorView, CanceledTripView, WaitingForGPSView, NoDataView } from './ETADisplay/StatusViews';
import BoardingInfoModal from './common/BoardingInfoModal';

// Custom hooks
import { useETADisplayData } from '../hooks/useETADisplayData';
import { useWaypointTranslations } from '../hooks/useWaypointTranslations';
import { useRelevantStops } from '../hooks/useRelevantStops';

// Utility functions
import {
  getTranslatedWaypointName,
  getTranslatedBoardingInfo,
  resolveBoardingImageUrl,
  hasBoardingInfo
} from '../utils/etaWaypointHelpers';
import { isEtaStopDisplaySkipped } from '../utils/etaStopDisplayState';

const normalizeDeclaration = (declaration) => {
    const waypointId = Number(declaration?.waypoint_id);
    const delayMinutes = Number(declaration?.delay_minutes);
    if (
        !Number.isInteger(waypointId)
        || waypointId <= 0
        || !['arrival', 'departure'].includes(declaration?.boundary_kind)
        || !Number.isInteger(delayMinutes)
        || delayMinutes <= 0
        || typeof declaration?.boundary_crossed !== 'boolean'
    ) return null;
    return {
        waypoint_id: waypointId,
        boundary_kind: declaration.boundary_kind,
        delay_minutes: delayMinutes,
        boundary_crossed: declaration.boundary_crossed
    };
};

const normalizeWaypointOrderVersion = (value) => (
    Number.isInteger(value) && value >= 0 ? value : null
);

const isStructurallySkipped = (stop) => (
    stop?.is_skipped === true || stop?.is_manual_skip === true
);

/**
 * Resolve declaration labels against the current viewer's visible rows. The
 * declaration remains owned by its original boundary; `placement` describes
 * only where this render discloses it and never feeds ETA or distance math.
 */
export const buildOperationalDelayCarriers = ({
    declarations = [],
    relevantStops = [],
    tripStops = [],
    declarationWaypointOrderVersion = null,
    effectiveStopsWaypointOrderVersion = null
} = {}) => {
    const projectionVersion = normalizeWaypointOrderVersion(
        declarationWaypointOrderVersion
    );
    const effectiveStopsVersion = normalizeWaypointOrderVersion(
        effectiveStopsWaypointOrderVersion
    );
    // Neither local nor fallback placement is safe without both proofs. A
    // missing proof is not an implicit legacy-zero version.
    if (projectionVersion === null || effectiveStopsVersion === null) {
        return new Map();
    }

    const effectiveById = new Map();
    for (const [position, stop] of (Array.isArray(tripStops) ? tripStops : []).entries()) {
        const waypointId = Number(stop?.waypoint_id);
        if (!Number.isInteger(waypointId) || waypointId <= 0 || effectiveById.has(waypointId)) {
            continue;
        }
        effectiveById.set(waypointId, { position, stop });
    }

    const visibleById = new Map();
    const orderedVisible = [];
    const visibleStops = Array.isArray(relevantStops) ? relevantStops : [];
    for (const [index, stop] of visibleStops.entries()) {
        const waypointId = Number(stop?.waypoint_id);
        const effective = effectiveById.get(waypointId);
        if (!Number.isInteger(waypointId) || !effective || visibleById.has(waypointId)) continue;
        const rendererSkipped = isEtaStopDisplaySkipped({
            stop,
            index,
            totalStops: visibleStops.length
        });
        const eligible = !rendererSkipped;
        const visible = { waypointId, position: effective.position, eligible };
        visibleById.set(waypointId, visible);
        orderedVisible.push(visible);
    }
    orderedVisible.sort((left, right) => left.position - right.position);

    const carriers = new Map();
    const seen = new Set();
    for (const candidate of Array.isArray(declarations) ? declarations : []) {
        const declaration = normalizeDeclaration(candidate);
        if (!declaration) continue;
        const declarationKey = `${declaration.waypoint_id}:${declaration.boundary_kind}`;
        if (seen.has(declarationKey)) continue;
        seen.add(declarationKey);

        const declarationBoundary = effectiveById.get(declaration.waypoint_id);
        // A stale visible ETA row cannot restore a waypoint that current proven
        // effective_stops removed or bypassed. A skipped declaration boundary
        // is suppressed rather than relocated.
        if (!declarationBoundary || isStructurallySkipped(declarationBoundary.stop)) continue;

        const local = visibleById.get(declaration.waypoint_id);
        // If the declaration row is present but the renderer classifies that
        // exact snapshot as skipped, suppress it. Relocating past a stale
        // rendered skip would turn contradictory topology snapshots into a
        // seemingly authoritative label on a later stop.
        if (local && !local.eligible) continue;
        const isLocal = local?.eligible === true;
        const carrier = isLocal
            ? local
            : projectionVersion === effectiveStopsVersion
                ? orderedVisible.find(({ position, eligible }) => (
                    eligible && position > declarationBoundary.position
                ))
                : null;
        if (!carrier) continue;

        const existing = carriers.get(carrier.waypointId) || [];
        existing.push({
            ...declaration,
            placement: isLocal ? 'local' : 'fallback'
        });
        carriers.set(carrier.waypointId, existing);
    }
    return carriers;
};

/**
 * ETADisplay Component
 *
 * Main component for displaying trip ETA information in various states:
 * - Live/Real-time ETA for active trips
 * - Estimated ETA for scheduled trips
 * - Completed trip comparisons
 * - Various status states (loading, error, canceled, no data)
 *
 * @param {Object} props - Component props
 * @param {Object} props.etaData - ETA data containing estimated, actual, or websocket information
 * @param {boolean} props.isLoading - Loading state indicator
 * @param {Object} props.error - Error object if any
 * @param {Object} props.tripData - Trip information including status, stops, booking details
 * @param {Object} props.currentEntity - Current entity/organization context
 * @param {boolean} props.isDriver - Whether the user is a driver
 * @param {boolean} props.gpsActive - Whether GPS is active
 * @param {Object} props.entitySettings - Entity-specific settings
 * @param {Object} props.waypointStates - State information for each waypoint
 * @param {boolean} props.canManualOverride - Whether user can manually override waypoint status
 * @param {boolean} props.canManualSkip - Whether user can communicate a manual stop skip
 * @param {Function} props.onManualArrival - Handler for manual arrival marking
 * @param {Function} props.onManualDeparture - Handler for manual departure marking
 * @param {Function} props.onManualSkip - Handler for manual skip
 * @param {Function} props.onManualCorrection - Handler for manual correction (reset to pending)
 * @param {Function} props.onPreTripManualCorrection - Direct pre-trip manual-skip correction
 * @param {string} props.connectionStatus - WebSocket connection status ('connected', 'reconnecting', 'stale_reconnecting', etc.)
 */
const ETADisplay = ({
    etaData,
    isLoading,
    error,
    tripData,
    currentEntity,
    isDriver = false,
    gpsActive = false,
    entitySettings = null,
    waypointStates = {},
    canManualOverride = false,
    canManualSkip = false,
    onManualArrival = null,
    onManualDeparture = null,
    onManualSkip = null,
    onManualCorrection = null,
    onPreTripManualCorrection = null,
    connectionStatus = null,
    showWaypoints = false,
    setShowWaypoints = null,
    tripPlan = null,
    entityTimeZone = null,
}) => {
    // State for UI controls (showWaypoints now comes from props, lifted to TripTracker)
    const [boardingInfoModal, setBoardingInfoModal] = useState({
        isOpen: false,
        waypointId: null,
        waypointName: '',
        initialBoardingInfo: null
    });
    const lastResumeRefreshTimestampRef = useRef(null);

    console.log("etaData in ETADisplay:", etaData);
    console.log("tripData in ETADisplay:", tripData);
    console.log("currentEntity in ETADisplay:", currentEntity);

    // Check for stale ETA data after app resume
    useEffect(() => {
        const resumeTimestamp = etaData?.websocket?._resume_timestamp || null;
        if (
            etaData?.websocket?._stale_after_resume
            && tripData?.status === 'in_progress'
            && resumeTimestamp
            && lastResumeRefreshTimestampRef.current !== resumeTimestamp
        ) {
            lastResumeRefreshTimestampRef.current = resumeTimestamp;
            console.log(`[ETADisplay] Detected stale ETA data after app resume, timestamp: ${resumeTimestamp}`);

            // Create a custom event to request ETA refresh
            const refreshEvent = new CustomEvent('requestETARefresh', {
                detail: {
                    tripId: tripData.id,
                    entityId: currentEntity?.id,
                    reason: 'stale_after_resume'
                }
            });
            window.dispatchEvent(refreshEvent);
        }
    }, [etaData?.websocket?._resume_timestamp, etaData?.websocket?._stale_after_resume, tripData?.id, tripData?.status, currentEntity?.id]);

    // Determine user's pickup and dropoff stop IDs
    const { pickupStopId, dropoffStopId } = useMemo(() => {
        if (tripData?.user_booking) {
            return {
                pickupStopId: tripData.user_booking.pickup_stop_id,
                dropoffStopId: tripData.user_booking.dropoff_stop_id
            };
        }
        return { pickupStopId: null, dropoffStopId: null };
    }, [tripData?.user_booking]);

    // Use custom hook to determine display data based on trip status
    const displayData = useETADisplayData(
        etaData,
        tripData,
        gpsActive,
        isDriver,
        pickupStopId,
        dropoffStopId,
        tripPlan,
        entityTimeZone
    );

    // Use custom hook to fetch waypoint translations
    const waypointTranslations = useWaypointTranslations(
        displayData,
        entitySettings,
        currentEntity
    );

    // Use custom hook to calculate relevant stops with dynamic distances
    const relevantStops = useRelevantStops(
        displayData,
        isDriver,
        pickupStopId,
        dropoffStopId,
        tripData,
        showWaypoints,
        etaData
    );
    const tripEffectiveStops = tripData?.effective_stops;
    const tripEffectiveStopsWaypointOrderVersion =
        tripData?.effective_stops_waypoint_order_version;
    const operationalDelayCarriers = useMemo(() => buildOperationalDelayCarriers({
        declarations: displayData?.operationalDelayDeclarations,
        relevantStops,
        tripStops: Array.isArray(tripEffectiveStops)
            ? tripEffectiveStops
            : [],
        declarationWaypointOrderVersion:
            displayData?.operationalDelayProjectionWaypointOrderVersion,
        effectiveStopsWaypointOrderVersion:
            tripEffectiveStopsWaypointOrderVersion
    }), [
        displayData?.operationalDelayDeclarations,
        displayData?.operationalDelayProjectionWaypointOrderVersion,
        relevantStops,
        tripEffectiveStops,
        tripEffectiveStopsWaypointOrderVersion
    ]);

    // Helper function to get translated waypoint name
    const handleGetTranslatedWaypointName = (waypointId, originalName) => {
        return getTranslatedWaypointName(
            waypointId,
            originalName,
            waypointTranslations,
            entitySettings,
            currentEntity
        );
    };

    // Helper function to get translated boarding info
    const handleGetTranslatedBoardingInfo = (waypointId, originalBoardingInfo) => {
        return getTranslatedBoardingInfo(
            waypointId,
            originalBoardingInfo,
            waypointTranslations,
            entitySettings,
            currentEntity
        );
    };

    // Open boarding info modal with initial data
    const handleOpenBoardingInfo = (waypoint) => {
        setBoardingInfoModal({
            isOpen: true,
            waypointId: waypoint.waypoint_id,
            waypointName: handleGetTranslatedWaypointName(
                waypoint.waypoint_id,
                waypoint.waypoint_name || waypoint.name
            ),
            initialBoardingInfo: {
                text: handleGetTranslatedBoardingInfo(
                    waypoint.waypoint_id,
                    waypoint.boarding_location_info
                ) || null,
                url: waypoint.boarding_location_url || null,
                imageUrl: resolveBoardingImageUrl(waypoint.boarding_location_image),
                hasInfo: hasBoardingInfo(waypoint)
            }
        });
    };

    // Close boarding info modal
    const handleCloseBoardingInfo = () => {
        setBoardingInfoModal({
            isOpen: false,
            waypointId: null,
            waypointName: '',
            initialBoardingInfo: null
        });
    };

    // Render appropriate view based on state
    // An ETA request failure must not remove the active trip's known stops or
    // manual passage controls. The unavailable view already explains its state.
    const hasUnavailableActiveStops = displayData?.type === 'active_unavailable' && relevantStops.length > 0;
    if (isLoading && !hasUnavailableActiveStops) {
        return <LoadingView />;
    }

    if (error && !hasUnavailableActiveStops) {
        return <ErrorView error={error} />;
    }

    if (displayData?.type === 'canceled') {
        return <CanceledTripView />;
    }

    if (displayData?.type === 'completed_comparison' || displayData?.type === 'completed_estimated_only') {
        return (
            <>
                <CompletedTripView
                    displayData={displayData}
                    relevantStops={relevantStops}
                    isDriver={isDriver}
                    showWaypoints={showWaypoints}
                    setShowWaypoints={setShowWaypoints}
                    pickupStopId={pickupStopId}
                    dropoffStopId={dropoffStopId}
                    getTranslatedWaypointName={handleGetTranslatedWaypointName}
                    handleOpenBoardingInfo={handleOpenBoardingInfo}
                    hasBoardingInfo={hasBoardingInfo}
                    entitySettings={entitySettings}
                    currentEntity={currentEntity}
                    entityTimeZone={entityTimeZone}
                />
                <BoardingInfoModal
                    isOpen={boardingInfoModal.isOpen}
                    onClose={handleCloseBoardingInfo}
                    waypointId={boardingInfoModal.waypointId}
                    waypointName={boardingInfoModal.waypointName}
                    entityId={currentEntity?.entityId || currentEntity?.id}
                    initialBoardingInfo={boardingInfoModal.initialBoardingInfo}
                />
            </>
        );
    }

    if (displayData?.type === 'waiting_for_map_api') {
        return <WaitingForGPSView />;
    }

    if (displayData?.type === 'no_data' || relevantStops.length === 0) {
        return <NoDataView displayData={displayData} tripData={tripData} isDriver={isDriver} />;
    }

    // Active trip view (scheduled, dispatched, in_progress)
    return (
        <>
            <LiveTripView
                displayData={displayData}
                relevantStops={relevantStops}
                isDriver={isDriver}
                showWaypoints={showWaypoints}
                setShowWaypoints={setShowWaypoints}
                pickupStopId={pickupStopId}
                dropoffStopId={dropoffStopId}
                tripData={tripData}
                getTranslatedWaypointName={handleGetTranslatedWaypointName}
                handleOpenBoardingInfo={handleOpenBoardingInfo}
                waypointStates={waypointStates}
                canManualOverride={canManualOverride}
                canManualSkip={canManualSkip}
                onManualArrival={onManualArrival}
                onManualDeparture={onManualDeparture}
                onManualSkip={onManualSkip}
                onManualCorrection={onManualCorrection}
                onPreTripManualCorrection={onPreTripManualCorrection}
                connectionStatus={connectionStatus}
                entityTimeZone={entityTimeZone}
                operationalDelayCarriers={operationalDelayCarriers}
            />
            <BoardingInfoModal
                isOpen={boardingInfoModal.isOpen}
                onClose={handleCloseBoardingInfo}
                waypointId={boardingInfoModal.waypointId}
                waypointName={boardingInfoModal.waypointName}
                entityId={currentEntity?.entityId || currentEntity?.id}
                initialBoardingInfo={boardingInfoModal.initialBoardingInfo}
            />
        </>
    );
};

export default ETADisplay;
